import React, { useEffect, useState } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Button, InputNumber, message, Space, Switch, Typography, Tag, Tooltip, Divider } from "antd"
import { RefreshCw, Globe, CheckCircle, TrendingUp, TrendingDown } from "react-feather"
import { useTranslation } from "react-i18next"
import { UPDATE_CURRENCY_RATE } from "../../../../rest-api/mutations/currencyRate"
import { GET_CURRENCY_RATE, GET_CBU_RATE } from "../../../../rest-api/queries/currencyRate"

export default function CurrencyRate() {
  const { t } = useTranslation()
  const [currencyRate, setCurrencyRate] = useState<number>(0)
  const [cbuRate, setCbuRate] = useState<number | null>(null)
  const [cbuDate, setCbuDate] = useState<string>("")
  const [cbuDiff, setCbuDiff] = useState<string>("")
  const [cbuLoading, setCbuLoading] = useState<boolean>(false)
  const [autoUpdateCbu, setAutoUpdateCbu] = useState<boolean>(() => {
    return localStorage.getItem("crm_cbu_auto_update") === "true"
  })

  const {
    mutate: updateCurrencyRate,
    isLoading: uLoading,
    error: uError,
  } = useMutation(UPDATE_CURRENCY_RATE, {
    onSuccess: () => {
      message.success(t("msgRateSaved"))
      refetch()
    },
    onError: () => {
      message.error(t("msgRateSaveFailed"))
    },
  })

  const {
    isLoading: gLoading,
    data: gData,
    refetch,
  } = useQuery(["GET_CURRENCY_RATE"], GET_CURRENCY_RATE)

  useEffect(() => {
    if (gData?.uzs) {
      setCurrencyRate(gData?.uzs)
    }
  }, [gData])

  const fetchCbuRate = async (autoApply: boolean = false) => {
    setCbuLoading(true)
    try {
      const res = await GET_CBU_RATE(autoApply)
      if (res?.rate) {
        setCbuRate(res.rate)
        setCbuDate(res.date)
        setCbuDiff(res.diff)
        if (autoApply) {
          setCurrencyRate(res.rate)
          refetch()
          message.success(`MB kursi (${res.rate.toLocaleString()} so'm) avtomatik o'rnatildi!`)
        } else {
          message.info(`MB rasmiy kursi: 1 USD = ${res.rate.toLocaleString()} UZS`)
        }
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Markaziy bank kursini olishda xatolik")
    } finally {
      setCbuLoading(false)
    }
  }

  useEffect(() => {
    // Initial fetch of CBU rate
    fetchCbuRate(autoUpdateCbu)

    // If auto-update is active, check periodically
    if (autoUpdateCbu) {
      const interval = setInterval(() => {
        fetchCbuRate(true)
      }, 10 * 60 * 1000) // every 10 mins
      return () => clearInterval(interval)
    }
  }, [autoUpdateCbu])

  const handleApplyCbuRate = () => {
    if (cbuRate) {
      setCurrencyRate(cbuRate)
      updateCurrencyRate({ uzs: cbuRate })
    } else {
      fetchCbuRate(true)
    }
  }

  const handleToggleAutoUpdate = (checked: boolean) => {
    setAutoUpdateCbu(checked)
    localStorage.setItem("crm_cbu_auto_update", String(checked))
    if (checked) {
      fetchCbuRate(true)
      message.success(t("msgAutoRateOn"))
    } else {
      message.info("Avto-yangilash o'chirildi")
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Space size={12} wrap align="center">
        <InputNumber
          style={{ minWidth: "170px", borderRadius: 6 }}
          min={1}
          value={currencyRate}
          onChange={(value) => setCurrencyRate(value || 0)}
          disabled={uLoading || gLoading}
          placeholder={t("phRateUZS")}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
          parser={(value) => Number(value?.replace(/\s?|(,*)/g, "") || 0)}
        />
        <Button
          onClick={() => updateCurrencyRate({ uzs: currencyRate })}
          type="primary"
          loading={uLoading}
          disabled={gLoading}
          style={{ borderRadius: 6, backgroundColor: "#2563EB" }}
        >
          {t("save")}
        </Button>

        <Button
          icon={<RefreshCw size={14} className={cbuLoading ? "spin-icon" : ""} style={{ marginRight: 6 }} />}
          onClick={() => fetchCbuRate(false)}
          loading={cbuLoading}
          style={{ borderRadius: 6 }}
        >
          MB kursini olish
        </Button>

        {cbuRate && (
          <Button
            type="dashed"
            icon={<CheckCircle size={14} color="#10B981" style={{ marginRight: 6 }} />}
            onClick={handleApplyCbuRate}
            style={{ borderRadius: 6 }}
          >
            MB kursini o'rnatish ({cbuRate.toLocaleString()} so'm)
          </Button>
        )}
      </Space>

      {/* Auto-update Switch and CBU Status Info */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          padding: "10px 14px",
          background: "rgba(37, 99, 235, 0.06)",
          borderRadius: 8,
          border: "1px solid rgba(37, 99, 235, 0.15)",
        }}
      >
        <Space align="center">
          <Globe size={16} color="#2563EB" />
          <div>
            <Typography.Text strong style={{ fontSize: 13, display: "block" }}>
              Markaziy Bank kursi bilan avtomatik sinxronlash
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {cbuRate ? (
                <>
                  Rasmiy kurs: <strong>1 USD = {cbuRate.toLocaleString()} UZS</strong> ({cbuDate})
                  {cbuDiff && (
                    <Tag
                      color={Number(cbuDiff) >= 0 ? "success" : "error"}
                      style={{ marginLeft: 8, fontSize: 11 }}
                    >
                      {Number(cbuDiff) >= 0 ? "+" : ""}
                      {cbuDiff} UZS
                    </Tag>
                  )}
                </>
              ) : (
                "Markaziy Bankning kunlik kursi bo'yicha doimiy yangilanib turadi"
              )}
            </Typography.Text>
          </div>
        </Space>

        <Space align="center">
          <span style={{ fontSize: 13, fontWeight: 500 }}>Avto-yangilash:</span>
          <Switch
            checked={autoUpdateCbu}
            onChange={handleToggleAutoUpdate}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
        </Space>
      </div>
    </div>
  )
}
